import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Layers,
  Plus,
  Trash2,
  Wand2,
  Shirt,
  Footprints,
  Gem,
  Hash,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { inventoryService } from '@/services/inventory';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { cn } from '@/lib/utils';
import type {
  InventoryItem,
  SkuStyle,
  VariantSpec,
  VariantTemplateEntry,
} from '@/types/inventory';

interface Props {
  item: InventoryItem | null;
  /**
   * Optional category + item name hint used to rank presets. The form
   * sheet hosting this section already knows the category name (it
   * loads categoriesQuery for the dropdown) so we pass it through
   * instead of duplicating the fetch here.
   */
  categoryHint?: string;
}

// ── SKU style segments ───────────────────────────────────────────────
//
// The four parent-level segments that compose the middle of a variant
// SKU, between the parent SKU and the variant size codes:
//
//   parentSKU(4) · gender(1) · fabric(1) · category(2) · colour(2) · size(2)
//
// Gender / fabric / category / colour are fixed per parent — one
// colour per parent, not a variant axis. The operator keys in a code
// (digits) + a human label for each.
type StyleKey = 'gender' | 'fabric' | 'category' | 'colour';

const STYLE_SEGMENTS: Array<{
  key: StyleKey;
  label: string;
  width: number;
  placeholderLabel: string;
  placeholderCode: string;
  hint: string;
}> = [
  { key: 'gender',   label: 'Gender',   width: 1, placeholderLabel: 'Men',    placeholderCode: '1',  hint: 'Men / Women / Unisex / Kids' },
  { key: 'fabric',   label: 'Fabric',   width: 1, placeholderLabel: 'Cotton', placeholderCode: '1',  hint: 'Cotton / Polyester / Linen…' },
  { key: 'category', label: 'Category', width: 2, placeholderLabel: 'Shirt',  placeholderCode: '01', hint: 'Shirt / Trouser / Jacket…' },
  { key: 'colour',   label: 'Colour',   width: 2, placeholderLabel: 'Yellow', placeholderCode: '06', hint: 'One colour per parent SKU' },
];

/** Compose the 6-digit style block: gender(1)+fabric(1)+category(2)+colour(2). */
function composeStyleBlock(style: SkuStyle): string {
  return (
    (style.gender?.code ?? '') +
    (style.fabric?.code ?? '') +
    (style.category?.code ?? '') +
    (style.colour?.code ?? '')
  );
}

// Curated attribute catalogue for the variant template builder. These
// are the true variant axes — Size and friends. Colour is NOT here:
// it's a parent-level style segment, not a variant axis.
const ATTRIBUTE_CATALOG: Record<string, string[]> = {
  Size:    ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'],
  Waist:   ['28', '30', '32', '34', '36', '38', '40', '42', '44', '46'],
  Length:  ['Short', 'Regular', 'Tall'],
  Fit:     ['Slim', 'Regular', 'Relaxed', 'Loose', 'Oversized'],
  Sleeve:  ['Sleeveless', 'Short', 'Half', 'Three-quarter', 'Full'],
  Neck:    ['Round', 'V-neck', 'Polo', 'Henley', 'Mock', 'Crew'],
  Shoe:    ['5', '6', '7', '8', '9', '10', '11', '12'],
  Metal:   ['Gold', 'Silver', 'Rose gold', 'Platinum', 'Stainless steel'],
};
const CUSTOM_ATTR_SENTINEL = '__custom__';

// Quick-preset catalogue. One click seeds the variant template (the
// size axis). Colour is set separately in the SKU style panel.
type Preset = {
  id: string;
  label: string;
  description: string;
  categoryKeywords: string[];
  icon: 'shirt' | 'pants' | 'shoes' | 'jewellery';
  template: VariantTemplateEntry[];
};

const VARIANT_PRESETS: Preset[] = [
  {
    id: 'apparel-size',
    label: 'Apparel sizes',
    description: 'S / M / L / XL / XXL',
    categoryKeywords: [
      'shirt', 'tee', 't-shirt', 'tshirt', 'top', 'tops', 'kurta', 'kurti',
      'blouse', 'dress', 'jacket', 'hoodie', 'sweatshirt', 'sweater',
      'apparel', 'clothing', 'wear', 'garment', 'saree', 'lehenga', 'salwar',
    ],
    icon: 'shirt',
    template: [{ name: 'Size', options: ['S', 'M', 'L', 'XL', 'XXL'] }],
  },
  {
    id: 'pants-numeric',
    label: 'Pants: waist (28–44)',
    description: 'Even waist sizes — India SMB common',
    categoryKeywords: [
      'pant', 'pants', 'trouser', 'trousers', 'jean', 'jeans', 'chino',
      'chinos', 'denim', 'short', 'shorts', 'bottom', 'bottoms', 'legging',
    ],
    icon: 'pants',
    template: [
      { name: 'Waist', options: ['28', '30', '32', '34', '36', '38', '40', '42', '44'] },
    ],
  },
  {
    id: 'shoes-uk',
    label: 'Shoes: UK sizes (6–12)',
    description: 'Whole UK sizes',
    categoryKeywords: [
      'shoe', 'shoes', 'sneaker', 'sneakers', 'boot', 'boots', 'sandal',
      'sandals', 'slipper', 'loafer', 'heel', 'footwear',
    ],
    icon: 'shoes',
    template: [
      { name: 'Shoe', options: ['6', '7', '8', '9', '10', '11', '12'] },
    ],
  },
  {
    id: 'jewellery-metal-size',
    label: 'Jewellery: metal × size',
    description: 'Gold/Silver/Rose-gold × 6/7/8/9/10',
    categoryKeywords: [
      'jewel', 'jewelry', 'jewellery', 'ring', 'rings', 'necklace',
      'bracelet', 'earring', 'pendant', 'gold', 'silver',
    ],
    icon: 'jewellery',
    template: [
      { name: 'Metal', options: ['Gold', 'Silver', 'Rose gold'] },
      { name: 'Size', options: ['6', '7', '8', '9', '10'] },
    ],
  },
];

const PRESET_ICON_TONE: Record<
  Preset['icon'],
  { Icon: LucideIcon; bg: string; fg: string }
> = {
  shirt:     { Icon: Shirt,      bg: 'bg-primary/10',    fg: 'text-primary' },
  pants:     { Icon: Layers,     bg: 'bg-info/10',       fg: 'text-info' },
  shoes:     { Icon: Footprints, bg: 'bg-amber-500/10',  fg: 'text-amber-600' },
  jewellery: { Icon: Gem,        bg: 'bg-purple-500/10', fg: 'text-purple-600' },
};

/**
 * Variants section for the item detail sheet.
 *
 * SKU shape:
 *   parentSKU(4) + gender(1) + fabric(1) + category(2) + colour(2) + size(2)
 *
 * The first four post-parent segments (the "SKU style") are fixed per
 * parent — keyed in once in the SKU style panel. The variant template
 * carries only the true variant axes (Size, etc.), each 2-digit.
 */
export function ItemVariantsSection({ item, categoryHint }: Props) {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const tenantCurrency = me?.tenantCurrency ?? 'INR';
  const isParent = !!item && !item.parentItemId;

  const [template, setTemplate] = useState<VariantTemplateEntry[]>(
    () => (item?.variantTemplate as VariantTemplateEntry[] | undefined) ?? [],
  );
  const [attributeCodes, setAttributeCodes] = useState<
    Record<string, Record<string, string>>
  >(
    () =>
      (item?.attributeCodes as Record<string, Record<string, string>> | undefined) ?? {},
  );
  const [style, setStyle] = useState<SkuStyle>(
    () => (item?.skuStyle as SkuStyle | undefined) ?? {},
  );

  // Re-sync the style block from the server after the parent mutation
  // lands (e.g. another tab saved it, or generate() persisted it).
  useEffect(() => {
    setStyle((item?.skuStyle as SkuStyle | undefined) ?? {});
  }, [item?.skuStyle]);

  const variantsQuery = useQuery({
    queryKey: ['inventory', 'variants', item?.id],
    queryFn: () => inventoryService.listVariants(item!.id),
    enabled: !!item && isParent,
  });

  const saveStyle = useMutation({
    mutationFn: () => inventoryService.update(item!.id, { skuStyle: style }),
    onSuccess: () => {
      toast.success('SKU style saved');
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save SKU style'),
  });

  const saveTemplate = useMutation({
    mutationFn: () => inventoryService.saveVariantTemplate(item!.id, template),
    onSuccess: () => {
      toast.success('Template saved');
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });

  const createVariants = useMutation({
    mutationFn: (specs: VariantSpec[]) => inventoryService.createVariants(item!.id, specs),
    onSuccess: (created) => {
      toast.success(`Created ${created.length} variant${created.length === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['inventory', 'variants', item?.id] });
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not create variants'),
  });

  const existingVariants = variantsQuery.data ?? [];
  const combos = useMemo(() => cartesian(template), [template]);
  const existingKeys = useMemo(() => {
    const set = new Set<string>();
    for (const v of existingVariants) {
      if (v.variantAttributes) set.add(comboKey(v.variantAttributes as Record<string, string>));
    }
    return set;
  }, [existingVariants]);
  const newCombos = combos.filter((c) => !existingKeys.has(comboKey(c)));

  // ─── Rules of Hooks ─────────────────────────────────────────────────
  // Every hook MUST be called above the early return below.
  const hint = useMemo(
    () => `${categoryHint ?? ''} ${item?.name ?? ''}`.toLowerCase().trim(),
    [categoryHint, item?.name],
  );
  const { recommended, other } = useMemo(() => {
    const scored = VARIANT_PRESETS.map((p) => {
      const score = p.categoryKeywords.reduce(
        (n, kw) => (hint.includes(kw) ? n + 1 : n),
        0,
      );
      return { preset: p, score };
    });
    return {
      recommended: scored
        .filter((s) => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((s) => s.preset),
      other: scored.filter((s) => s.score === 0).map((s) => s.preset),
    };
  }, [hint]);

  if (!item) return null;

  if (item.parentItemId) {
    return (
      <div className="rounded-md border border-info/20 bg-info/5 px-3 py-3 text-[12.5px]">
        <span className="font-medium">This is a variant.</span>{' '}
        <span className="text-muted-foreground">
          Catalog metadata + commercial defaults are inherited from the parent SKU.
        </span>
      </div>
    );
  }

  // ── SKU style completeness ──────────────────────────────────────────
  // Every segment needs a code of its exact width before variant SKUs
  // can be generated — a short style block would corrupt the SKU shape.
  const styleComplete = STYLE_SEGMENTS.every(
    (s) => (style[s.key]?.code ?? '').length === s.width,
  );
  const styleBlock = composeStyleBlock(style);

  const setStyleSegment = (
    key: StyleKey,
    patch: Partial<{ code: string; label: string }>,
  ) =>
    setStyle((s) => ({
      ...s,
      [key]: {
        code: s[key]?.code ?? '',
        label: s[key]?.label ?? '',
        ...patch,
      },
    }));

  // ── Template builder handlers ───────────────────────────────────────
  const addAttribute = () =>
    setTemplate((t) => [...t, { name: '', options: [] }]);
  const removeAttribute = (idx: number) => {
    const removed = template[idx]?.name;
    setTemplate((t) => t.filter((_, i) => i !== idx));
    if (removed) {
      setAttributeCodes(({ [removed]: _, ...rest }) => rest);
    }
  };
  const setName = (idx: number, name: string) => {
    const oldName = template[idx]?.name;
    setTemplate((t) => t.map((e, i) => (i === idx ? { ...e, name } : e)));
    if (oldName && oldName !== name) {
      setAttributeCodes((prev) => {
        const { [oldName]: codes, ...rest } = prev;
        return codes ? { ...rest, [name]: codes } : rest;
      });
    }
  };
  const toggleOption = (idx: number, value: string) => {
    const v = value.trim();
    if (!v) return;
    const attrName = template[idx]?.name;
    const wasSelected = template[idx]?.options.includes(v);
    setTemplate((t) =>
      t.map((e, i) => {
        if (i !== idx) return e;
        const has = e.options.includes(v);
        return {
          ...e,
          options: has ? e.options.filter((o) => o !== v) : [...e.options, v],
        };
      }),
    );
    if (attrName && wasSelected) {
      setAttributeCodes((prev) => {
        const codes = prev[attrName];
        if (!codes || !(v in codes)) return prev;
        const { [v]: _drop, ...rest } = codes;
        return { ...prev, [attrName]: rest };
      });
    }
  };
  const addCustomOption = (idx: number, value: string) => {
    const v = value.trim();
    if (!v) return;
    setTemplate((t) =>
      t.map((e, i) =>
        i === idx && !e.options.includes(v)
          ? { ...e, options: [...e.options, v] }
          : e,
      ),
    );
  };
  const setOptionCode = (idx: number, value: string, code: string) => {
    const attrName = template[idx]?.name;
    if (!attrName) return;
    setAttributeCodes((prev) => {
      const next = { ...prev };
      const codes = { ...(next[attrName] ?? {}) };
      const trimmed = code.trim();
      if (trimmed) codes[value] = trimmed;
      else delete codes[value];
      if (Object.keys(codes).length > 0) next[attrName] = codes;
      else delete next[attrName];
      return next;
    });
  };

  /**
   * Variant SKU = parentSku + style block + variant codes.
   * Every variant code is 2-digit (position-based unless the operator
   * typed an explicit code). Must stay in sync with the BE
   * `buildVariantSku` helper character-for-character.
   */
  const buildSku = (combo: Record<string, string>): string => {
    const codes: string[] = [];
    for (const entry of template) {
      const val = combo[entry.name];
      if (val === undefined) continue;
      const explicit = attributeCodes[entry.name]?.[val];
      if (explicit) {
        codes.push(explicit);
        continue;
      }
      const idx = entry.options.indexOf(val);
      codes.push(String(idx + 1).padStart(2, '0'));
    }
    return item.sku + styleBlock + codes.join('');
  };

  const applyPreset = (preset: Preset) =>
    setTemplate(preset.template.map((e) => ({ name: e.name, options: [...e.options] })));

  const generate = async () => {
    if (!styleComplete) {
      toast.error('Complete the SKU style (gender / fabric / category / colour) first');
      return;
    }
    const validTemplate = template.filter(
      (e) => e.name.trim() && e.options.length > 0,
    );
    if (validTemplate.length === 0) {
      toast.error('Define at least one variant attribute (e.g. Size) with options');
      return;
    }
    // Fill a 2-digit code map for every option, merging explicit codes.
    const filled: Record<string, Record<string, string>> = {};
    for (const entry of validTemplate) {
      const existing = attributeCodes[entry.name] ?? {};
      filled[entry.name] = {};
      entry.options.forEach((opt, idx) => {
        filled[entry.name][opt] =
          existing[opt] ?? String(idx + 1).padStart(2, '0');
      });
    }
    try {
      await inventoryService.update(item.id, {
        attributeCodes: filled,
        skuStyle: style,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
      return;
    }
    setAttributeCodes(filled);
    const specs: VariantSpec[] = newCombos.map((combo) => {
      const codes: string[] = [];
      for (const entry of validTemplate) {
        const val = combo[entry.name];
        if (val === undefined) continue;
        codes.push(filled[entry.name]?.[val] ?? '00');
      }
      return {
        sku: item.sku + styleBlock + codes.join(''),
        attributes: combo,
      };
    });
    // Guard: two combos must not resolve to the same SKU. This happens when
    // two options share a code (e.g. XL and XXL both "05"), which the BE would
    // reject as a 409 "SKU already exists" — surface the real cause here so the
    // operator knows to fix the attribute code, not chase a phantom duplicate.
    const skuToCombo = new Map<string, string>();
    for (const s of specs) {
      const label = Object.entries(s.attributes)
        .map(([k, v]) => `${k} ${v}`)
        .join(' / ');
      const prev = skuToCombo.get(s.sku);
      if (prev) {
        toast.error(
          `"${prev}" and "${label}" both map to SKU ${s.sku}. Give each option a unique code in the attribute editor, then Generate again.`,
        );
        return;
      }
      skuToCombo.set(s.sku, label);
    }
    createVariants.mutate(specs);
  };

  return (
    <div className="space-y-5">
      {/* ── SKU style panel ─────────────────────────────────────────── */}
      <section className="space-y-3 rounded-md border border-border/70 bg-card p-4">
        <div className="flex items-center gap-2">
          <Hash className="h-3.5 w-3.5 text-primary" />
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            SKU style
          </div>
        </div>
        <div className="rounded-md border border-info/20 bg-info/[0.04] px-3 py-2 text-[11px] text-muted-foreground">
          A variant SKU is{' '}
          <span className="font-mono text-foreground">
            parent · gender · fabric · category · colour · size
          </span>
          . The four segments below are fixed for this whole product — key
          in a code (digits) and a label for each.
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          {STYLE_SEGMENTS.map((seg) => {
            const code = style[seg.key]?.code ?? '';
            const bad = code.length > 0 && code.length !== seg.width;
            return (
              <div
                key={seg.key}
                className="space-y-1.5 rounded-md border border-border/60 bg-muted/10 p-2.5"
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-[11.5px] font-medium text-foreground">
                    {seg.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {seg.width}-digit code
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input
                    value={style[seg.key]?.label ?? ''}
                    onChange={(e) =>
                      setStyleSegment(seg.key, { label: e.target.value })
                    }
                    placeholder={seg.placeholderLabel}
                    maxLength={64}
                    className="h-7 flex-1 text-[12px]"
                  />
                  <Input
                    value={code}
                    onChange={(e) =>
                      setStyleSegment(seg.key, {
                        code: e.target.value.replace(/\D/g, '').slice(0, seg.width),
                      })
                    }
                    placeholder={seg.placeholderCode}
                    className={cn(
                      'h-7 w-14 text-center font-mono text-[12px]',
                      bad && 'border-destructive/60',
                    )}
                  />
                </div>
                <div className="text-[10px] text-muted-foreground">{seg.hint}</div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-t border-border/60 pt-3">
          <div className="text-[11.5px] text-muted-foreground">
            {styleComplete ? (
              <>
                Variant SKU prefix:{' '}
                <span className="font-mono font-semibold text-foreground">
                  {item.sku}
                  {styleBlock}
                </span>{' '}
                <span className="text-muted-foreground">+ size</span>
              </>
            ) : (
              <span className="text-warning">
                Fill every segment with a code of the right length to enable
                variant generation.
              </span>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => saveStyle.mutate()}
            disabled={saveStyle.isPending}
          >
            {saveStyle.isPending ? 'Saving…' : 'Save SKU style'}
          </Button>
        </div>
      </section>

      {/* ── Quick-pick presets ──────────────────────────────────────── */}
      {template.length === 0 && (
        <section className="space-y-4 rounded-lg border border-primary/20 bg-gradient-to-br from-primary/[0.06] via-primary/[0.02] to-background p-4">
          {recommended.length > 0 ? (
            <>
              <div className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <div className="text-[11.5px] font-semibold text-primary">
                  Recommended {categoryHint ? `for ${categoryHint}` : 'presets'}
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {recommended.map((preset) => (
                  <PresetCard key={preset.id} preset={preset} accent onClick={() => applyPreset(preset)} />
                ))}
              </div>
              {other.length > 0 && (
                <details className="group">
                  <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">
                    Show {other.length} other preset{other.length === 1 ? '' : 's'}
                  </summary>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {other.map((preset) => (
                      <PresetCard key={preset.id} preset={preset} onClick={() => applyPreset(preset)} />
                    ))}
                  </div>
                </details>
              )}
            </>
          ) : (
            <>
              <div className="text-[11.5px] font-semibold text-foreground">
                Start from a preset
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {VARIANT_PRESETS.map((preset) => (
                  <PresetCard key={preset.id} preset={preset} onClick={() => applyPreset(preset)} />
                ))}
              </div>
            </>
          )}
          <div className="text-[11px] text-muted-foreground">
            Or define your own variant attributes below.
          </div>
        </section>
      )}

      {/* ── Variant template builder ────────────────────────────────── */}
      <section className="space-y-3 rounded-md border border-border/70 bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            Variant attributes
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addAttribute}>
            <Plus className="h-3 w-3" />
            Add attribute
          </Button>
        </div>

        {template.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/70 px-3 py-4 text-center text-[12px] text-muted-foreground">
            No variant attributes yet. Add one (e.g. Size, then pick S / M / L).
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-info/20 bg-info/[0.04] px-3 py-2 text-[11px] text-muted-foreground">
              <strong className="text-foreground">How it works:</strong> pick an
              attribute, then tap each option you want. Codes auto-fill (2 digits)
              in their own column — edit them only if your barcode scheme expects
              specific digits.
            </div>
            {template.map((entry, idx) => (
              <AttributeEditor
                key={idx}
                entry={entry}
                codes={attributeCodes[entry.name] ?? {}}
                onRename={(name) => setName(idx, name)}
                onRemove={() => removeAttribute(idx)}
                onToggleOption={(value) => toggleOption(idx, value)}
                onAddCustom={(value) => addCustomOption(idx, value)}
                onCodeChange={(value, code) => setOptionCode(idx, value, code)}
              />
            ))}
          </div>
        )}

        {template.length > 0 && (
          <div className="flex items-center justify-between border-t border-border/60 pt-3">
            <div className="text-[11.5px] text-muted-foreground">
              {combos.length} combination{combos.length === 1 ? '' : 's'} ·{' '}
              {existingVariants.length} already created ·{' '}
              <strong className="text-foreground">{newCombos.length} to add</strong>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => saveTemplate.mutate()}
              disabled={saveTemplate.isPending}
            >
              {saveTemplate.isPending ? 'Saving…' : 'Save template'}
            </Button>
          </div>
        )}
      </section>

      {/* ── Generate ────────────────────────────────────────────────── */}
      {newCombos.length > 0 && (
        <section className="space-y-3 rounded-md border border-border/70 bg-card p-4">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Generate variant SKUs
            </div>
            <Button
              type="button"
              size="sm"
              onClick={generate}
              disabled={createVariants.isPending || !styleComplete}
            >
              <Wand2 className="h-3.5 w-3.5" />
              {createVariants.isPending
                ? 'Generating…'
                : `Generate ${newCombos.length} ${newCombos.length === 1 ? 'SKU' : 'SKUs'}`}
            </Button>
          </div>
          {!styleComplete ? (
            <div className="rounded-md border border-warning/30 bg-warning/[0.06] px-3 py-2 text-[11.5px] text-foreground">
              Complete the SKU style panel above before generating variants.
            </div>
          ) : (
            <>
              <div className="text-[11px] text-muted-foreground">
                Parent SKU{' '}
                <span className="font-mono text-foreground">{item.sku}</span> +
                style block{' '}
                <span className="font-mono text-foreground">{styleBlock}</span> +
                size code.
              </div>
              <div className="overflow-hidden rounded-md border border-border/60">
                <div className="grid grid-cols-12 gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                  <div className="col-span-7">Combination</div>
                  <div className="col-span-5">Full variant SKU</div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {newCombos.slice(0, 50).map((combo, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-2 border-b border-border/60 px-3 py-1.5 text-[11.5px] last:border-0"
                    >
                      <div className="col-span-7 flex flex-wrap items-center gap-1">
                        {Object.entries(combo).map(([k, v]) => (
                          <Badge key={k} variant="muted" className="text-[10px]">
                            <span className="text-muted-foreground">{k}:</span>
                            <span className="ml-1 font-medium">{v}</span>
                          </Badge>
                        ))}
                      </div>
                      <div className="col-span-5 truncate font-mono font-semibold text-foreground">
                        {buildSku(combo)}
                      </div>
                    </div>
                  ))}
                  {newCombos.length > 50 && (
                    <div className="px-3 py-2 text-center text-[11px] text-muted-foreground">
                      + {newCombos.length - 50} more
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {/* ── Existing variants grid ──────────────────────────────────── */}
      <section className="space-y-3 rounded-md border border-border/70 bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            <Layers className="mr-1 inline h-3 w-3" />
            Variants
          </div>
          <span className="text-[11px] text-muted-foreground">
            {existingVariants.length} total
          </span>
        </div>
        <div className="rounded-md border border-info/20 bg-info/[0.04] px-3 py-2 text-[11.5px] text-muted-foreground">
          <strong className="text-foreground">Cascade rules:</strong> brand,
          manufacturer, tax, HSN, UoM and dimensions inherit from the parent
          automatically. <strong className="text-foreground">Stock + price +
          images</strong> are set per variant — click <strong>Manage</strong> to
          deep-edit a variant.
        </div>
        {variantsQuery.isLoading ? (
          <div className="text-[12px] text-muted-foreground">Loading…</div>
        ) : existingVariants.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/70 px-3 py-4 text-center text-[12px] text-muted-foreground">
            No variants generated yet. Define attributes above and hit Generate.
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border/60">
            <div className="grid grid-cols-12 gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <div className="col-span-3">SKU</div>
              <div className="col-span-3">Attributes</div>
              <div className="col-span-2 text-right">Stock</div>
              <div className="col-span-3 text-right">Price ({tenantCurrency})</div>
              <div className="col-span-1" />
            </div>
            {existingVariants.map((v) => (
              <VariantGridRow
                key={v.id}
                variant={v}
                tenantCurrency={tenantCurrency}
                parentSellingPrice={item.sellingPrice ?? null}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Variant grid row ──────────────────────────────────────────────

function VariantGridRow({
  variant,
  tenantCurrency,
  parentSellingPrice,
}: {
  variant: InventoryItem;
  tenantCurrency: string;
  parentSellingPrice: string | number | null;
}) {
  const qc = useQueryClient();
  const [price, setPrice] = useState<string>(
    variant.sellingPrice != null ? String(variant.sellingPrice) : '',
  );
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      inventoryService.update(variant.id, {
        sellingPrice: price === '' ? undefined : Number(price),
      }),
    onSuccess: () => {
      toast.success(`${variant.sku} updated`);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['inventory', 'variants', variant.parentItemId] });
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: () =>
      inventoryService.removeVariant(variant.parentItemId!, variant.id),
    onSuccess: () => {
      toast.success(`${variant.sku} removed`);
      qc.invalidateQueries({ queryKey: ['inventory', 'variants', variant.parentItemId] });
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not remove variant'),
  });

  const inheritedPriceLabel =
    parentSellingPrice != null
      ? `Inherits ${tenantCurrency} ${Number(parentSellingPrice).toLocaleString()}`
      : 'Inherits parent';

  const onHand = variant.totalOnHand ?? 0;
  const available = variant.totalAvailable ?? 0;
  const reserved = variant.totalReserved ?? 0;

  return (
    <div className="grid grid-cols-12 items-center gap-2 border-b border-border/60 px-3 py-1.5 last:border-0">
      <div className="col-span-3 truncate font-mono text-[11.5px] text-foreground" title={variant.sku}>
        {variant.sku}
      </div>
      <div className="col-span-3 flex flex-wrap gap-1">
        {Object.entries(variant.variantAttributes ?? {}).map(([k, val]) => (
          <Badge key={k} variant="muted" className="text-[10px]">
            {String(val)}
          </Badge>
        ))}
      </div>
      <div className="col-span-2 text-right font-mono text-[11.5px] tabular-nums">
        <span className={cn(onHand === 0 ? 'text-destructive' : 'text-foreground')}>
          {onHand.toLocaleString()}
        </span>
        {reserved > 0 && (
          <div
            className="text-[10px] text-muted-foreground"
            title={`Reserved: ${reserved}, Available: ${available}`}
          >
            {available} avail
          </div>
        )}
      </div>
      <div className="col-span-3 flex items-center justify-end gap-1.5">
        <Input
          value={price}
          onChange={(e) => {
            setPrice(e.target.value);
            setDirty(true);
          }}
          placeholder={inheritedPriceLabel}
          className="h-7 w-32 text-right font-mono text-[11.5px]"
          inputMode="decimal"
        />
        {dirty && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[10.5px]"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? '…' : 'Save'}
          </Button>
        )}
      </div>
      <div className="col-span-1 flex items-center justify-end gap-1">
        <Badge
          variant={variant.status === 'ACTIVE' ? 'success' : 'muted'}
          className="text-[10px]"
          title={variant.status}
        >
          {variant.status === 'ACTIVE' ? 'ok' : variant.status.slice(0, 3).toLowerCase()}
        </Badge>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => setConfirmOpen(true)}
          disabled={remove.isPending}
          className="h-6 w-6 text-muted-foreground hover:text-destructive"
          title={`Remove ${variant.sku}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Remove variant ${variant.sku}?`}
        description={
          <>
            It will be archived. Orders and invoices that already reference it
            stay intact, but it disappears from this list and won't show in
            catalog browsing.
          </>
        }
        confirmLabel="Remove variant"
        onConfirm={() => remove.mutateAsync()}
      />
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function cartesian(template: VariantTemplateEntry[]): Record<string, string>[] {
  const valid = template.filter((e) => e.name.trim() && e.options.length > 0);
  if (valid.length === 0) return [];
  let out: Record<string, string>[] = [{}];
  for (const entry of valid) {
    const next: Record<string, string>[] = [];
    for (const existing of out) {
      for (const opt of entry.options) {
        next.push({ ...existing, [entry.name]: opt });
      }
    }
    out = next;
  }
  return out;
}

function comboKey(c: Record<string, string>): string {
  return Object.entries(c)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join('|');
}

// Single preset card.
function PresetCard({
  preset,
  accent,
  onClick,
}: {
  preset: Preset;
  accent?: boolean;
  onClick: () => void;
}) {
  const { Icon, bg, fg } = PRESET_ICON_TONE[preset.icon];
  const totalCombos = preset.template.reduce(
    (n, e) => n * (e.options.length || 1),
    1,
  );
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex h-full flex-col items-start gap-2 rounded-md border bg-background p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm',
        accent
          ? 'border-primary/40 ring-1 ring-primary/20 hover:border-primary/60'
          : 'border-border/60 hover:border-primary/40',
      )}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <div className={cn('grid h-8 w-8 place-items-center rounded-md', bg)}>
          <Icon className={cn('h-4 w-4', fg)} />
        </div>
        <Badge variant="muted" className="text-[10px] tabular-nums">
          {totalCombos} SKU{totalCombos === 1 ? '' : 's'}
        </Badge>
      </div>
      <div className="space-y-0.5">
        <div className="text-[12.5px] font-semibold leading-tight">{preset.label}</div>
        <div className="text-[10.5px] leading-snug text-muted-foreground">
          {preset.description}
        </div>
      </div>
      <div className="mt-auto flex flex-wrap gap-1 pt-1">
        {preset.template.map((e) => (
          <Badge key={e.name} variant="outline" className="text-[9.5px] font-normal">
            {e.name} · {e.options.length}
          </Badge>
        ))}
      </div>
    </button>
  );
}

// ── Controlled attribute editor ──────────────────────────────────────

function AttributeEditor({
  entry,
  codes,
  onRename,
  onRemove,
  onToggleOption,
  onAddCustom,
  onCodeChange,
}: {
  entry: VariantTemplateEntry;
  codes: Record<string, string>;
  onRename: (name: string) => void;
  onRemove: () => void;
  onToggleOption: (value: string) => void;
  onAddCustom: (value: string) => void;
  onCodeChange: (value: string, code: string) => void;
}) {
  const [customMode, setCustomMode] = useState(
    () => !!entry.name && !(entry.name in ATTRIBUTE_CATALOG),
  );
  const [customDraft, setCustomDraft] = useState('');

  const catalogOptions = ATTRIBUTE_CATALOG[entry.name] ?? [];
  const allOptions = useMemo(() => {
    const seen = new Set(catalogOptions);
    const extras = entry.options.filter((o) => !seen.has(o));
    return [...catalogOptions, ...extras];
  }, [catalogOptions, entry.options]);

  const handleAttrSelect = (value: string) => {
    if (value === CUSTOM_ATTR_SENTINEL) {
      setCustomMode(true);
      onRename('');
    } else {
      setCustomMode(false);
      onRename(value);
    }
  };

  const submitCustom = () => {
    const v = customDraft.trim();
    if (!v) return;
    onAddCustom(v);
    setCustomDraft('');
  };

  return (
    <div className="space-y-3 rounded-md border border-border/60 bg-muted/10 p-3">
      <div className="grid grid-cols-12 items-start gap-2">
        <div className="col-span-11 space-y-2">
          <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Attribute
          </div>
          {customMode ? (
            <div className="flex items-center gap-2">
              <Input
                value={entry.name}
                onChange={(e) => onRename(e.target.value)}
                placeholder="Attribute name (e.g. Strap, Pocket)"
                maxLength={32}
                className="h-8 text-[12.5px]"
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCustomMode(false);
                  onRename('');
                }}
                className="h-8 px-2 text-[11px]"
              >
                Use catalog
              </Button>
            </div>
          ) : (
            <Select
              value={entry.name in ATTRIBUTE_CATALOG ? entry.name : ''}
              onValueChange={handleAttrSelect}
            >
              <SelectTrigger className="h-8 text-[12.5px]">
                <SelectValue placeholder="Pick an attribute" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(ATTRIBUTE_CATALOG).map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_ATTR_SENTINEL}>
                  <span className="italic text-muted-foreground">Custom…</span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="col-span-1 flex items-center justify-end pt-5">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onRemove}
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            aria-label="Remove attribute"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {entry.name && (
        <div className="space-y-2">
          <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Options · tap to {entry.options.length > 0 ? 'toggle' : 'select'}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {allOptions.length === 0 ? (
              <div className="text-[11.5px] italic text-muted-foreground">
                No catalog options for this attribute — add custom values below.
              </div>
            ) : (
              allOptions.map((opt) => {
                const selected = entry.options.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => onToggleOption(opt)}
                    className={cn(
                      'rounded-md border px-2 py-1 text-[11.5px] transition-colors',
                      selected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border/70 bg-card text-muted-foreground hover:border-border hover:text-foreground',
                    )}
                  >
                    {opt}
                  </button>
                );
              })
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={customDraft}
              onChange={(e) => setCustomDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitCustom();
                }
              }}
              placeholder="+ Add custom option"
              maxLength={32}
              className="h-7 max-w-[200px] text-[11.5px]"
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={submitCustom}
              disabled={!customDraft.trim()}
              className="h-7 px-2 text-[11px]"
            >
              Add
            </Button>
          </div>
        </div>
      )}

      {entry.name && entry.options.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Codes — 2-digit, used in the variant SKU
          </div>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-4">
            {entry.options.map((opt) => (
              <div
                key={opt}
                className="flex items-center gap-1.5 rounded-md border border-border/40 bg-card px-2 py-1"
              >
                <span className="flex-1 truncate text-[11.5px]">{opt}</span>
                <Input
                  value={codes[opt] ?? ''}
                  onChange={(e) =>
                    onCodeChange(opt, e.target.value.replace(/\D/g, '').slice(0, 2))
                  }
                  placeholder="01"
                  maxLength={2}
                  className="h-6 w-12 px-1.5 text-center font-mono text-[11px]"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
